import React, { FC, useState } from "react";
import { Pageable } from "types/Blog";
import twFocusClass from "utils/twFocusClass";

export interface PaginationProps {
  className?: string;
  onPageChange?: (page: Pageable) => void;
  totalElements?: number;
  pageSize?: number;
}

const Pagination: FC<PaginationProps> = ({
  className = "",
  onPageChange,
  totalElements = 0,
  pageSize = 24,
}) => {
  const [pageNo, setPageNo] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);

  const totalPages = Math.ceil(totalElements / rowsPerPage);
  const from = totalElements === 0 ? 0 : pageNo * rowsPerPage + 1;
  const to = Math.min((pageNo + 1) * rowsPerPage, totalElements);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    onPageChange?.({ pageNo: newPage, pageSize: rowsPerPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPageNo(0);
    onPageChange?.({ pageNo: 0, pageSize: newSize });
  };

  if (totalElements <= 0) return null;

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mt-6 text-sm ${className}`}
    >
      <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 sm:gap-6 w-full">
        {/* Rows per page selector */}
        <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
          <label htmlFor="pagination-rows-per-page" className="text-xs sm:text-sm font-medium">
            Show me more Blogs:
          </label>
          <select
            id="pagination-rows-per-page"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            aria-label="Select blogs per page"
            className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 text-sm py-1 px-2.5 focus:ring-1 focus:ring-primary-500 focus:outline-none"
          >
            {[8, 12, 24, 48, 96].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Displayed range */}
        <div className="text-neutral-700 dark:text-neutral-300 font-medium text-xs sm:text-sm">
          {from}–{to} of {totalElements}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => handlePageChange(pageNo - 1)}
            disabled={pageNo <= 0}
            aria-label="Go to previous page"
            className={`w-11 h-11 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed ${twFocusClass()}`}
          >
            <i className="las la-angle-left text-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => handlePageChange(pageNo + 1)}
            disabled={to >= totalElements || pageNo >= totalPages - 1}
            aria-label="Go to next page"
            className={`w-11 h-11 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed ${twFocusClass()}`}
          >
            <i className="las la-angle-right text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
